/**
 * Single source of truth for Firestore paths. Both clients, Cloud Functions and
 * the rules tests build paths from here, so a layout change is one edit.
 */

export const COLLECTION = {
  HOTELS: 'hotels',
  ROOMS: 'rooms',
  SESSIONS: 'sessions',
  RECEPTIONISTS: 'receptionists',
  DEVICES: 'devices',
  CALLS: 'calls',
  CALL_EVENTS: 'events',
  OWNER_DEVICES: 'ownerDevices',
  STAFF: 'staff',
  BOOKINGS: 'bookings',
  GUESTS: 'guests',
  SETTINGS: 'settings',
  MENU_ITEMS: 'items',
  REQUESTS: 'requests',
  FEEDBACKS: 'feedbacks',
  OFFERS: 'offers',
  PLACES: 'places',
  TOUR_PACKAGES: 'tourPackages',
} as const;

/** Document ids under `hotels/{hotelId}/settings`. */
export const SETTINGS_DOC = {
  HOUSEKEEPING: 'housekeeping',
  LAUNDRY: 'laundry',
  MENU: 'menu',
  WIFI: 'wifi',
  STORE: 'store',
  PLACES_TO_EXPLORE: 'placesToExplore',
} as const;

export const hotelPath = (hotelId: string) =>
  `${COLLECTION.HOTELS}/${hotelId}`;

export const roomsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.ROOMS}`;

export const roomPath = (hotelId: string, roomId: string) =>
  `${roomsPath(hotelId)}/${roomId}`;

export const guestSessionsPath = (hotelId: string, roomId: string) =>
  `${roomPath(hotelId, roomId)}/${COLLECTION.SESSIONS}`;

export const guestSessionPath = (
  hotelId: string,
  roomId: string,
  guestUid: string,
) => `${guestSessionsPath(hotelId, roomId)}/${guestUid}`;

export const receptionistsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.RECEPTIONISTS}`;

export const receptionistPath = (hotelId: string, receptionistId: string) =>
  `${receptionistsPath(hotelId)}/${receptionistId}`;

export const devicesPath = (hotelId: string, receptionistId: string) =>
  `${receptionistPath(hotelId, receptionistId)}/${COLLECTION.DEVICES}`;

export const devicePath = (
  hotelId: string,
  receptionistId: string,
  deviceId: string,
) => `${devicesPath(hotelId, receptionistId)}/${deviceId}`;

export const callsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.CALLS}`;

export const callPath = (hotelId: string, callId: string) =>
  `${callsPath(hotelId)}/${callId}`;

/* -------------------------------------------------------------------------- */
/* Hotel operations (rooms management, staff, bookings)                       */
/* -------------------------------------------------------------------------- */

export const staffCollectionPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.STAFF}`;

export const staffPath = (hotelId: string, staffId: string) =>
  `${staffCollectionPath(hotelId)}/${staffId}`;

/**
 * A staff member's push tokens: `hotels/{hotelId}/staff/{staffId}/devices`.
 *
 * Deliberately a third device collection rather than a reuse of the
 * receptionist one. `receptionists/{uid}/devices` belongs to the call system
 * and is what `sendIncomingCall` rings; housekeeping has no row there at all,
 * and giving them one to carry a request notification would make them a ring
 * candidate as a side effect. So call devices ring and staff devices notify,
 * and a receptionist — who holds both documents anyway — registers the same
 * token in both, one per purpose.
 */
export const staffDevicesPath = (hotelId: string, staffId: string) =>
  `${staffPath(hotelId, staffId)}/${COLLECTION.DEVICES}`;

export const staffDevicePath = (hotelId: string, staffId: string, deviceId: string) =>
  `${staffDevicesPath(hotelId, staffId)}/${deviceId}`;

export const bookingsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.BOOKINGS}`;

export const bookingPath = (hotelId: string, bookingId: string) =>
  `${bookingsPath(hotelId)}/${bookingId}`;

/**
 * The guest register: one document per phone number the hotel has checked in.
 *
 * `guestId` is derived from the phone (see `guestIdForPhone`), which is what
 * lets a returning guest be found with a single `get` rather than a query, and
 * lets `checkIn` upsert the row inside its transaction without a lookup race.
 */
export const guestsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.GUESTS}`;

export const guestPath = (hotelId: string, guestId: string) =>
  `${guestsPath(hotelId)}/${guestId}`;

/* -------------------------------------------------------------------------- */
/* Guest services (owner-configured)                                          */
/* -------------------------------------------------------------------------- */

export const settingsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.SETTINGS}`;

export const housekeepingSettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.HOUSEKEEPING}`;

export const laundrySettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.LAUNDRY}`;

export const menuSettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.MENU}`;

export const wifiSettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.WIFI}`;

/** The hotel store: categories on the document, one product per item document. */
export const storeSettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.STORE}`;

export const storeItemsPath = (hotelId: string) =>
  `${storeSettingsPath(hotelId)}/${COLLECTION.MENU_ITEMS}`;

export const storeItemPath = (hotelId: string, itemId: string) =>
  `${storeItemsPath(hotelId)}/${itemId}`;

/**
 * Places to Explore: the service document holds only `enabled`; nearby places
 * and tour packages are one document each beneath it — see
 * `types/placesToExplore.ts`.
 */
export const placesToExploreSettingsPath = (hotelId: string) =>
  `${settingsPath(hotelId)}/${SETTINGS_DOC.PLACES_TO_EXPLORE}`;

export const placesPath = (hotelId: string) =>
  `${placesToExploreSettingsPath(hotelId)}/${COLLECTION.PLACES}`;

export const placePath = (hotelId: string, placeId: string) =>
  `${placesPath(hotelId)}/${placeId}`;

export const tourPackagesPath = (hotelId: string) =>
  `${placesToExploreSettingsPath(hotelId)}/${COLLECTION.TOUR_PACKAGES}`;

export const tourPackagePath = (hotelId: string, packageId: string) =>
  `${tourPackagesPath(hotelId)}/${packageId}`;

/**
 * One document per dish, under the menu document rather than inline on it, so
 * editing a price rewrites one dish and not the whole menu.
 */
export const menuItemsPath = (hotelId: string) =>
  `${menuSettingsPath(hotelId)}/${COLLECTION.MENU_ITEMS}`;

export const menuItemPath = (hotelId: string, itemId: string) =>
  `${menuItemsPath(hotelId)}/${itemId}`;

/* -------------------------------------------------------------------------- */
/* Guest requests and feedback                                                */
/* -------------------------------------------------------------------------- */

/** One collection for housekeeping, laundry and restaurant — see `types/requests.ts`. */
export const requestsPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.REQUESTS}`;

export const requestPath = (hotelId: string, requestId: string) =>
  `${requestsPath(hotelId)}/${requestId}`;

export const feedbacksPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.FEEDBACKS}`;

export const feedbackPath = (hotelId: string, feedbackId: string) =>
  `${feedbacksPath(hotelId)}/${feedbackId}`;

/* -------------------------------------------------------------------------- */
/* Offers (owner-configured, display only)                                    */
/* -------------------------------------------------------------------------- */

/** One document per banner — see `types/offers.ts`. */
export const offersPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/${COLLECTION.OFFERS}`;

export const offerPath = (hotelId: string, offerId: string) =>
  `${offersPath(hotelId)}/${offerId}`;

/* -------------------------------------------------------------------------- */
/* Cloud Storage                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Storage layout mirrors the Firestore hierarchy so one Storage rule per
 * subtree covers it. Guest identity documents sit under their own prefix
 * because they are the only genuinely sensitive files here -- see storage.rules.
 */
export const roomMediaStoragePath = (hotelId: string, roomId: string) =>
  `hotels/${hotelId}/rooms/${roomId}`;

export const guestDocumentStoragePath = (hotelId: string, bookingRef: string) =>
  `hotels/${hotelId}/bookings/${bookingRef}/documents`;

/** The hotel's logo. One folder, so replacing it can clean up the old file. */
export const hotelLogoStoragePath = (hotelId: string) => `hotels/${hotelId}/logo`;

/**
 * Restaurant menu photos. One flat folder per hotel rather than one per dish:
 * a photo is uploaded before the dish exists (the form uploads on pick, like
 * room photos), so there is no item id to nest it under yet.
 */
export const menuImageStoragePath = (hotelId: string) => `hotels/${hotelId}/menu`;

/** Store product photos, same arrangement as menu photos. */
export const storeImageStoragePath = (hotelId: string) => `hotels/${hotelId}/store`;

/** Offer banner backgrounds, one flat folder per hotel like menu photos. */
export const offerImageStoragePath = (hotelId: string) => `hotels/${hotelId}/offers`;

/**
 * Places to Explore photos: `hotels/{hotelId}/placesToExplore/{places|tourPackages}/{folderId}`.
 * `folderId` is the item id when editing and a client-made draft id when
 * adding — photos upload on pick, before the document exists, exactly as room
 * media does. Call with `folderId` omitted for the kind's prefix.
 */
export const placesToExploreImageStoragePath = (
  hotelId: string,
  kind: 'places' | 'tourPackages',
  folderId?: string,
) => `hotels/${hotelId}/placesToExplore/${kind}${folderId === undefined ? '' : `/${folderId}`}`;

/**
 * Owner push tokens: `hotels/{hotelId}/ownerDevices/{deviceId}`.
 *
 * Kept apart from the receptionist `devices` subcollection rather than merged
 * into it. That collection is owned by the call system, which rings every
 * device under a *receptionist* — putting the owner's handset there would make
 * the owner a call candidate as a side effect of installing the app.
 */
export const ownerDevicesPath = (hotelId: string) =>
  `${hotelPath(hotelId)}/ownerDevices`;

export const ownerDevicePath = (hotelId: string, deviceId: string) =>
  `${ownerDevicesPath(hotelId)}/${deviceId}`;
